import React, { useCallback } from 'react';
import { Table, InputNumber, Input, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { F9431 } from '../../../services/pw9233Api';

interface DetailGridProps {
  details: Partial<F9431>[];
  isEditing: boolean;
  names: Record<string, string>;
  updateDetailField: (rowIdx: number, field: keyof F9431, value: F9431[keyof F9431]) => void;
  onAdd: () => void;
  onDelete: (rowIdx: number) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const DetailGrid: React.FC<DetailGridProps> = ({
  details, isEditing, names,
  updateDetailField, onAdd, onDelete, resolveSingleName,
}) => {
  const handleSalaryCodeBlur = useCallback((idx: number, code: string) => {
    resolveSingleName(`salaryCode_${idx}`, 'salaryCode', code);
  }, [resolveSingleName]);

  const columns: ColumnsType<Partial<F9431>> = [
    {
      title: '項次',
      dataIndex: 'f9431_03',
      key: 'f9431_03',
      width: 55,
      render: (val: number, _rec, idx) => val ?? idx + 1,
    },
    {
      title: '薪資代號',
      dataIndex: 'f9431_11',
      key: 'salaryCode',
      width: 90,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input
            value={val || ''}
            maxLength={4}
            size="small"
            style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f9431_11', e.target.value)}
            onBlur={e => handleSalaryCodeBlur(idx, e.target.value)}
          />
        ) : val || '',
    },
    {
      title: '薪資名稱',
      key: 'salaryName',
      width: 120,
      render: (_val, rec, idx) => {
        const lookupName = names[`salaryCode_${idx}`];
        return lookupName || (rec as Partial<F9431>).f9431_11a || '';
      },
    },
    {
      title: '薪資金額',
      dataIndex: 'f9431_21',
      key: 'amount',
      width: 120,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber
            value={val ?? 0}
            min={0}
            precision={1}
            size="small"
            style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f9431_21', v ?? 0)}
          />
        ) : (val ?? 0).toLocaleString(),
    },
    {
      title: '備用數量',
      dataIndex: 'f9431_22',
      key: 'qty',
      width: 90,
      render: (val: number, _rec, idx) =>
        isEditing ? (
          <InputNumber
            value={val ?? 0}
            precision={1}
            size="small"
            style={{ width: '100%' }}
            onChange={v => updateDetailField(idx, 'f9431_22', v ?? 0)}
          />
        ) : (val ?? 0),
    },
    {
      title: '說明',
      dataIndex: 'f9431_23',
      key: 'desc',
      width: 200,
      render: (val: string, _rec, idx) =>
        isEditing ? (
          <Input
            value={val || ''}
            maxLength={60}
            size="small"
            style={{ width: '100%' }}
            onChange={e => updateDetailField(idx, 'f9431_23', e.target.value)}
          />
        ) : val || '',
    },
    ...(isEditing
      ? [
          {
            title: '操作',
            key: 'action',
            width: 60,
            render: (_val: unknown, _rec: Partial<F9431>, idx: number) => (
              <Button
                size="small"
                danger
                type="link"
                onClick={() => onDelete(idx)}
              >
                刪除
              </Button>
            ),
          } as ColumnsType<Partial<F9431>>[number],
        ]
      : []),
  ];

  return (
    <div className="pw9233-detail-grid">
      {isEditing && (
        <div className="pw9233-detail-toolbar">
          <Button size="small" type="dashed" onClick={onAdd}>
            + 薪資項目新增
          </Button>
        </div>
      )}
      <Table<Partial<F9431>>
        dataSource={details}
        columns={columns}
        rowKey={(_rec, idx) => idx ?? 0}
        size="small"
        pagination={false}
        scroll={{ y: 400 }}
      />
    </div>
  );
};

export default React.memo(DetailGrid);
